import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  View,
  Text,
  ScrollView,
  StyleSheet,
  Image,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { Toast } from "react-native-toast-notifications";
import { useDispatch, useSelector } from "react-redux";
import { router, useFocusEffect } from "expo-router";
import { RootState } from "../../store";
import { appActions } from "../../store/slices/appSlice";
import { useCart } from "../../hooks/useCart";
import { useStock } from "../../hooks/useStock";
import customAxios from "../../utils/customAxios";
import i18n from "../../locales/i18n";
import WebSidebar from "./WebSidebar";

// Import the sell image for header
import SellImage from "../../assets/images/sell.png";

// Import search icon
import SearchIcon from "../../assets/web/scanner/search.png";
import PlusIcon from "../../assets/web/scanner/plus_icon.png";

interface Product {
  id_product: number;
  name: string;
  price_sell: string;
  price_bought: string;
  stock: number;
  photo?: string;
  quantity?: number;
  barcode?: string | number;
  type_quantity?: string;
}

const WebSellScreen: React.FC = () => {
  const dispatch = useDispatch();
  const { locale } = useSelector((state: RootState) => state.app);

  // Check if current language is RTL (Arabic)
  const isRTL = locale === "ar" || i18n.locale === "ar";

  // Refs for inputs
  const searchInputRef = useRef<TextInput>(null);
  const barcodeInputRef = useRef<TextInput>(null);
  const unregisteredInputRef = useRef<TextInput>(null);
  const barcodeBufferRef = useRef<string>("");
  const barcodeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // State for editable quantity
  const [editingQuantityId, setEditingQuantityId] = useState<number | null>(
    null,
  );
  const [editingQuantityValue, setEditingQuantityValue] = useState("");

  // Hooks
  const {
    products: allStockProducts,
    isLoading: stockLoading,
    fetchProducts,
  } = useStock();

  const {
    selectedProducts,
    quantities,
    totalPrice,
    boughtPrice,
    addProduct,
    updateQuantity,
    removeProduct,
    calculateTotals,
    clearCart,
  } = useCart();

  // Local state
  const [searchQuery, setSearchQuery] = useState("");
  const [barcodeQuery, setBarcodeQuery] = useState("");
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [autherItem, setAutherItem] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch products on mount
  useFocusEffect(
    useCallback(() => {
      fetchProducts();
    }, [fetchProducts]),
  );

  // Calculate totals when cart changes
  useEffect(() => {
    calculateTotals();
  }, [quantities, selectedProducts, calculateTotals]);

  // Global keyboard listener for smart input routing
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Ignore if user is already typing in an input or editing quantity
      const activeElement = document.activeElement as HTMLElement;
      const isInInput =
        activeElement?.tagName === "INPUT" ||
        activeElement?.tagName === "TEXTAREA";
      const isEditing = editingQuantityId !== null;

      if (isInInput || isEditing) {
        return;
      }

      const key = event.key;

      // If it's a letter, focus search input
      if (/^[a-zA-Z\u0621-\u064A]$/.test(key)) {
        searchInputRef.current?.focus();
      }
      // If it's a number, focus barcode input (physical barcode scanners send digits)
      else if (/^\d$/.test(key)) {
        // Prepend the first digit so it's not lost when we redirect focus
        setBarcodeQuery((prev) => prev + key);
        barcodeInputRef.current?.focus();
        event.preventDefault();
      }
    };

    // Add event listener
    if (typeof window !== "undefined") {
      window.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("keydown", handleKeyDown);
      }
      if (barcodeTimeoutRef.current) {
        clearTimeout(barcodeTimeoutRef.current);
      }
    };
  }, [allStockProducts, editingQuantityId]);

  // Search products by name
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchQuery && searchQuery.trim()) {
        handleNameSearch(searchQuery);
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  const handleNameSearch = (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    const results = allStockProducts.filter((product: any) =>
      product.name.toLowerCase().includes(query.toLowerCase()),
    );
    setSearchResults(results as Product[]);
    setIsSearching(false);
  };

  const handleBarcodeSearch = (barcode: string) => {
    if (!barcode.trim()) return;

    const product = allStockProducts.find(
      (p: any) => String(p.barcode) === barcode.trim(),
    );

    if (product) {
      handleSelectProduct(product as Product);
      setBarcodeQuery("");
      Toast.show(`${i18n.t("productFound")} ${product.name}`, {
        type: "success",
      });
    } else {
      setBarcodeQuery("");
      Toast.show(i18n.t("productNotFound"), {
        type: "danger",
      });
    }
  };

  const handleBarcodeSubmit = () => {
    if (barcodeQuery.trim()) {
      handleBarcodeSearch(barcodeQuery);
    }
  };

  const handleSelectProduct = (product: Product) => {
    const existingIndex = selectedProducts.findIndex(
      (p: any) => p.id_product === product.id_product,
    );

    if (existingIndex === -1) {
      addProduct(product, 1);
      Toast.show(`${i18n.t("productAdded")} ${product.name}`, {
        type: "success",
      });
    } else {
      const currentQty = quantities[product.id_product] || 1;
      updateQuantity(product.id_product, currentQty + 1);
      Toast.show(`${i18n.t("quantityUpdated")} ${product.name}`, {
        type: "success",
      });
    }
    setSearchQuery("");
    setSearchResults([]);
  };

  const handleAddUnregisteredProduct = () => {
    if (!autherItem || !autherItem.match(/^-?\d*\.?\d+$/)) {
      Toast.show(i18n.t("enterValidValue"), { type: "danger" });
      return;
    }

    const uniqueId = Date.now();
    const otherProduct = {
      id_product: uniqueId,
      name: "other",
      price_bought: "0",
      price_sell: autherItem,
      stock: 1,
    };

    addProduct(otherProduct, 1);
    Toast.show(i18n.t("productAddedSuccess"), { type: "success" });
    setAutherItem("");
  };

  const handleRemoveFromCart = (productId: number) => {
    removeProduct(productId);
  };

  const handleConfirmSale = async () => {
    if (selectedProducts.length === 0) {
      Toast.show(i18n.t("searchForProduct"), { type: "danger" });
      return;
    }

    if (totalPrice < 0) {
      Toast.show(i18n.t("totalCannotBeNegative"), { type: "danger" });
      return;
    }

    setIsSubmitting(true);

    const allproducts = selectedProducts.map((item: Product) => ({
      id_product: item.name === "other" ? 0 : item.id_product,
      price_sell: item.price_sell,
      quantity: quantities[item.id_product] || 1,
      product_name: item.name,
    }));

    const profit = selectedProducts.reduce((acc: number, item: Product) => {
      if (item.name !== "other") {
        return (
          acc +
          (quantities[item.id_product] || 1) *
            (parseFloat(item.price_sell) - parseFloat(item.price_bought))
        );
      }
      return acc;
    }, 0);

    try {
      await customAxios.post("/sell/client/checkout", {
        total: totalPrice,
        profit: profit,
        capital: boughtPrice,
        listofproducts: allproducts,
      });

      Toast.show(i18n.t("saleSuccess"), { type: "success" });
      dispatch(appActions.toggleStatsRefresh());
      clearCart();
      setSearchQuery("");
      setBarcodeQuery("");
      setSearchResults([]);
    } catch (error: any) {
      if (error?.response?.status === 403) {
        const errorMessage = error?.response?.data?.error || "";
        if (
          typeof errorMessage === "string" &&
          errorMessage.toLowerCase().includes("free tier")
        ) {
          Toast.show(
            i18n.t("freeTierLimitExceeded") + " " + i18n.t("upgradeRequired"),
            {
              type: "danger",
              duration: 5000,
            },
          );
          setTimeout(() => {
            router.push("/(drawer)/params/limits");
          }, 2000);
          return;
        }
      }

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        i18n.t("billNotSaved");
      Toast.show(errorMessage, { type: "danger" });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Get display products - filter to show only products WITHOUT barcode (like phone pages)
  const displayProducts = searchQuery.trim()
    ? searchResults.filter(
        (p: Product) => !p.barcode || p.barcode === "" || p.barcode === 0,
      )
    : allStockProducts
        .filter((p: any) => !p.barcode || p.barcode === "" || p.barcode === 0)
        .slice(0, 20);

  // Handle quantity edit submit (no stock validation - user can enter any quantity)
  const handleQuantityEditSubmit = (productId: number) => {
    const newQty = parseInt(editingQuantityValue);

    if (!newQty || newQty <= 0) {
      Toast.show(i18n.t("enterValidQuantity"), {
        type: "danger",
      });
      setEditingQuantityId(null);
      setEditingQuantityValue("");
      return;
    }

    updateQuantity(productId, newQty);
    setEditingQuantityId(null);
    setEditingQuantityValue("");
  };

  return (
    <div
      key={`sell-screen-${locale}`}
      className="flex flex-1 bg-[#F5F5F5] min-h-screen flex-row"
    >
      <WebSidebar />

      <View style={styles.mainContent}>
        <ScrollView
          style={styles.scrollView}
          showsVerticalScrollIndicator={false}
        >
          <div
            className={`flex flex-row p-5 gap-5 ${isRTL ? "" : "flex-row-reverse"}`}
          >
            {/* Cart Panel - RIGHT in LTR, LEFT in RTL */}
            <View style={styles.leftPanel}>
              {/* Total Card */}
              <View style={styles.totalCard}>
                <Text style={styles.totalLabel}>{i18n.t("total")}</Text>
                <div className="flex flex-row items-baseline gap-2">
                  <Text style={styles.currency}>{i18n.t("currency")}</Text>
                  <Text style={styles.totalAmount}>
                    {totalPrice
                      .toString()
                      .replace(/\B(?=(\d{3})+(?!\d))/g, " ")}
                  </Text>
                </div>
              </View>

              {/* Cart Table */}
              <View style={styles.cartTable}>
                {/* Table Header */}
                <div className="flex flex-row bg-gray-100 py-3 px-2 border-b border-gray-200">
                  <View
                    style={[
                      styles.removeBtn,
                      { backgroundColor: "transparent" },
                    ]}
                  />
                  <Text style={[styles.tableHeaderCell, styles.priceColumn]}>
                    {i18n.t("price")}
                  </Text>
                  <Text style={[styles.tableHeaderCell, styles.qtyColumn]}>
                    {i18n.t("quantity")}
                  </Text>
                  <Text style={[styles.tableHeaderCell, styles.sizeColumn]}>
                    {i18n.t("typeOfQuantity")}
                  </Text>
                  <Text style={[styles.tableHeaderCell, styles.nameColumn]}>
                    {i18n.t("productname")}
                  </Text>
                </div>

                {/* Cart Items */}
                <ScrollView style={styles.cartItems} nestedScrollEnabled>
                  {selectedProducts.length === 0 ? (
                    <View style={styles.emptyCart}>
                      <Text style={styles.emptyCartText}>
                        {i18n.t("noproducts")}
                      </Text>
                    </View>
                  ) : (
                    selectedProducts.map((item: Product, index: number) => (
                      <div
                        key={item.id_product}
                        className={`flex flex-row py-3 px-2 items-center border-b border-gray-100 ${index % 2 === 1 ? "bg-gray-50" : ""}`}
                      >
                        <TouchableOpacity
                          style={styles.removeBtn}
                          onPress={() => handleRemoveFromCart(item.id_product)}
                        >
                          <Text style={styles.removeBtnText}>×</Text>
                        </TouchableOpacity>
                        <Text style={[styles.tableCell, styles.priceColumn]}>
                          {parseFloat(item.price_sell)
                            .toString()
                            .replace(/\B(?=(\d{3})+(?!\d))/g, " ")}{" "}
                          {i18n.t("currency")}
                        </Text>
                        <View style={[styles.qtyColumn, styles.qtyControls]}>
                          {editingQuantityId === item.id_product ? (
                            <input
                              type="number"
                              style={{
                                width: 60,
                                fontSize: 16,
                                fontWeight: 600,
                                color: "#333",
                                textAlign: "center",
                                border: "2px solid #1379A2",
                                borderRadius: 6,
                                padding: "6px 4px",
                                backgroundColor: "#fff",
                                outline: "none",
                              }}
                              value={editingQuantityValue}
                              onChange={(e) =>
                                setEditingQuantityValue(e.target.value)
                              }
                              autoFocus
                              onBlur={() =>
                                handleQuantityEditSubmit(item.id_product)
                              }
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleQuantityEditSubmit(item.id_product);
                                }
                                e.stopPropagation();
                              }}
                            />
                          ) : (
                            <TouchableOpacity
                              onPress={() => {
                                setEditingQuantityId(item.id_product);
                                setEditingQuantityValue(
                                  String(quantities[item.id_product] || 1),
                                );
                              }}
                              style={styles.qtyValueContainer}
                            >
                              <Text style={styles.qtyValue}>
                                {quantities[item.id_product] || 1}
                              </Text>
                            </TouchableOpacity>
                          )}
                        </View>
                        <Text style={[styles.tableCell, styles.sizeColumn]}>
                          {item.type_quantity || "-"}
                        </Text>
                        <Text style={[styles.tableCell, styles.nameColumn]}>
                          {item.name === "other"
                            ? `${item.price_sell} ${i18n.t("currency")}`
                            : item.name}
                        </Text>
                      </div>
                    ))
                  )}
                </ScrollView>
              </View>

              {/* Clear All Button */}
              <TouchableOpacity style={styles.clearButton} onPress={clearCart}>
                <Text style={styles.clearButtonText}>{i18n.t("delete")}</Text>
              </TouchableOpacity>

              {/* Confirm Button */}
              <TouchableOpacity
                style={styles.confirmButton}
                onPress={handleConfirmSale}
                disabled={isSubmitting || selectedProducts.length === 0}
              >
                {isSubmitting ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.confirmButtonText}>
                    {i18n.t("confirm")}
                  </Text>
                )}
              </TouchableOpacity>
            </View>

            {/* Products Panel - RIGHT in LTR, LEFT in RTL (due to browser RTL flip) */}
            <div className="flex-1 flex flex-col gap-4">
              {/* Header with blue bar */}
              <View style={styles.headerSection}>
                <div
                  className={`flex flex-row items-center gap-4 ${isRTL ? "justify-end flex-row-reverse" : "justify-start"}`}
                >
                  <View style={styles.headerIconContainer}>
                    <Image
                      source={SellImage}
                      style={styles.headerIcon}
                      resizeMode="contain"
                    />
                  </View>
                  <Text style={styles.headerTitle}>{i18n.t("newsell")}</Text>
                </div>
                <View style={styles.headerBar} />
              </View>

              {/* Search Input */}
              <div className="flex flex-row items-center bg-white rounded-lg border border-gray-200 px-3">
                <Image
                  source={SearchIcon}
                  style={styles.searchIcon}
                  resizeMode="contain"
                />
                <TextInput
                  ref={searchInputRef as any}
                  style={styles.searchInput}
                  placeholder={i18n.t("search")}
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  placeholderTextColor="#999"
                />
              </div>

              {/* Barcode Input with inline add button */}
              <div className="flex flex-row gap-3 items-stretch">
                <div className="flex flex-row items-center bg-white rounded-lg border border-gray-200 overflow-hidden">
                  <TextInput
                    ref={unregisteredInputRef as any}
                    style={styles.unregisteredInput}
                    placeholder={i18n.t("unregisteredProduct")}
                    value={autherItem}
                    onChangeText={setAutherItem}
                    onSubmitEditing={handleAddUnregisteredProduct}
                    keyboardType="numeric"
                    placeholderTextColor="#999"
                  />
                  <TouchableOpacity
                    style={styles.inlinePlusButton}
                    onPress={handleAddUnregisteredProduct}
                  >
                    <Text style={styles.plusButtonText}>+</Text>
                  </TouchableOpacity>
                </div>
                <div className="flex-1 flex flex-row items-center rounded-lg border border-gray-200 bg-white overflow-hidden">
                  <TextInput
                    ref={barcodeInputRef as any}
                    style={styles.barcodeInput}
                    placeholder={i18n.t("enterBarcode")}
                    value={barcodeQuery}
                    onChangeText={setBarcodeQuery}
                    onSubmitEditing={handleBarcodeSubmit}
                    keyboardType="numeric"
                    placeholderTextColor="#999"
                  />
                </div>
                <TouchableOpacity
                  style={styles.inlineAddButton}
                  onPress={handleBarcodeSubmit}
                >
                  <Text style={styles.addButtonText}>{i18n.t("add")}</Text>
                </TouchableOpacity>
              </div>

              {/* Scanner Status Indicator */}

              {/* Product Grid */}
              <View style={styles.productGridContainer}>
                {stockLoading || isSearching ? (
                  <View style={styles.loadingContainer}>
                    <ActivityIndicator size="large" color="#1379A2" />
                  </View>
                ) : displayProducts.length === 0 ? (
                  <View style={styles.emptyProductsContainer}>
                    <Text style={styles.emptyProductsText}>
                      {i18n.t("noProductsWithoutBarcode") ||
                        "لا توجد منتجات بدون باركود"}
                    </Text>
                  </View>
                ) : (
                  <ScrollView
                    style={styles.productGridScroll}
                    showsVerticalScrollIndicator={true}
                    nestedScrollEnabled
                  >
                    <View style={styles.productGrid}>
                      {displayProducts.map((product: Product) => (
                        <TouchableOpacity
                          key={product.id_product}
                          style={styles.productCard}
                          onPress={() => handleSelectProduct(product)}
                        >
                          {product.photo ? (
                            <Image
                              source={{ uri: product.photo }}
                              style={styles.productImage}
                              resizeMode="cover"
                            />
                          ) : (
                            <View style={styles.productImagePlaceholder}>
                              <Text style={styles.noImageText}>
                                {i18n.t("noImage")}
                              </Text>
                            </View>
                          )}
                          <Text style={styles.productName} numberOfLines={1}>
                            {product.name}
                          </Text>
                          <div className="flex flex-row items-center justify-between p-2">
                            <TouchableOpacity
                              style={styles.productAddBtn}
                              onPress={(e) => {
                                e.stopPropagation();
                                handleSelectProduct(product);
                              }}
                            >
                              <Text style={styles.productAddBtnText}>
                                {i18n.t("add")}
                              </Text>
                            </TouchableOpacity>
                            <Text style={styles.productPrice}>
                              {parseFloat(product.price_sell)
                                .toString()
                                .replace(/\B(?=(\d{3})+(?!\d))/g, " ")}{" "}
                              {i18n.t("currency")}
                            </Text>
                          </div>
                        </TouchableOpacity>
                      ))}
                    </View>
                  </ScrollView>
                )}
              </View>
            </div>
          </div>
        </ScrollView>
      </View>
    </div>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F5F5F5",
    minHeight: "100vh" as any,
  },
  containerRTL: {
    flexDirection: "row-reverse",
  } as any,
  mainContent: {
    flex: 1,
  },
  scrollView: {
    flex: 1,
  },
  contentWrapper: {
    flexDirection: "row",
    padding: 20,
    gap: 20,
  },
  // Left Panel Styles
  leftPanel: {
    width: 400,
    gap: 16,
  },
  totalCard: {
    borderRadius: 16,
    padding: 24,
    alignItems: "center",
    borderWidth: 3,
    borderColor: "#1379A2",
  },
  totalLabel: {
    fontSize: 20,
    color: "#1379A2",
    marginBottom: 8,
    fontWeight: "500",
  },
  totalRow: {
    flexDirection: "row",
    alignItems: "baseline",
    gap: 8,
  },
  totalAmount: {
    fontSize: 48,
    fontWeight: "bold",
  },
  currency: {
    fontSize: 28,
    fontWeight: "500",
  },
  cartTable: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    overflow: "hidden",
    borderWidth: 1,
    borderColor: "#E0E0E0",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#F5F5F5",
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderBottomWidth: 1,
    borderBottomColor: "#E0E0E0",
  },
  tableHeaderCell: {
    fontSize: 14,
    fontWeight: "600",
    color: "#666",
    textAlign: "center",
  },
  tableRow: {
    flexDirection: "row",
    paddingVertical: 12,
    paddingHorizontal: 8,
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#F0F0F0",
  },
  tableRowAlt: {
    backgroundColor: "#FAFAFA",
  },
  tableCell: {
    fontSize: 16,
    color: "#333",
    textAlign: "center",
  },
  nameColumn: {
    flex: 2,
    textAlign: "right",
  },
  sizeColumn: {
    flex: 1,
  },
  qtyColumn: {
    flex: 1,
  },
  priceColumn: {
    flex: 1.5,
  },
  removeBtn: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: "#E0E0E0",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
  },
  removeBtnText: {
    color: "#666",
    fontSize: 18,
    fontWeight: "bold",
  },
  qtyControls: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 6,
  },
  qtyBtn: {
    width: 24,
    height: 24,
    borderRadius: 4,
    backgroundColor: "#1379A2",
    alignItems: "center",
    justifyContent: "center",
  },
  qtyBtnDisabled: {
    backgroundColor: "#ccc",
  },
  qtyBtnText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "bold",
  },
  qtyBtnTextDisabled: {
    color: "#999",
  },
  qtyValue: {
    fontSize: 14,
    fontWeight: "600",
    color: "#333",
    minWidth: 24,
    textAlign: "center",
    paddingVertical: 2,
    paddingHorizontal: 4,
    cursor: "pointer" as any,
  },
  qtyValueContainer: {
    backgroundColor: "#f0f0f0",
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    minWidth: 40,
    alignItems: "center",
  },
  qtyInput: {
    width: 40,
    fontSize: 14,
    fontWeight: "600",
    color: "#333",
    textAlign: "center",
    borderWidth: 1,
    borderColor: "#1379A2",
    borderRadius: 4,
    paddingVertical: 2,
    paddingHorizontal: 4,
    backgroundColor: "#fff",
    outlineStyle: "none" as any,
  },
  cartItems: {
    maxHeight: 300,
  },
  emptyCart: {
    padding: 40,
    alignItems: "center",
  },
  emptyCartText: {
    color: "#999",
    fontSize: 16,
  },
  clearButton: {
    backgroundColor: "#555",
    borderRadius: 25,
    paddingVertical: 12,
    alignItems: "center",
    alignSelf: "center",
    paddingHorizontal: 40,
  },
  clearButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "500",
  },
  confirmButton: {
    backgroundColor: "#42B0DB",
    borderRadius: 12,
    paddingVertical: 16,
    alignItems: "center",
  },
  confirmButtonText: {
    color: "#FFFFFF",
    fontSize: 20,
    fontWeight: "600",
  },
  // Right Panel Styles
  rightPanel: {
    flex: 1,
    gap: 16,
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 16,
  },
  headerTitle: {
    fontSize: 32,
    fontWeight: "bold",
    color: "#333",
  },
  headerIcon: {
    width: 80,
    height: 80,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
    paddingHorizontal: 12,
  },
  searchInput: {
    flex: 1,
    paddingVertical: 8,
    fontSize: 16,
    textAlign: "right",
    outlineStyle: "none" as any,
  },
  searchIcon: {
    width: 20,
    height: 20,
    marginLeft: 8,
  },
  // Header Section styles (like dette screen)
  headerSection: {
    marginBottom: 12,
  },
  headerIconContainer: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: "#E8F4F8",
    borderWidth: 4,
    borderColor: "#1379A2",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  headerBar: {
    height: 8,
    backgroundColor: "#1379A2",
    borderRadius: 4,
    marginTop: 8,
  },
  barcodeRow: {
    flexDirection: "row",
    gap: 12,
    alignItems: "center",
  },
  barcodeInputContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
    overflow: "hidden",
  },
  inlineAddButton: {
    backgroundColor: "#359D4D",
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 20,
    width: 150,
    alignItems: "center",
    justifyContent: "center",
  },
  addButtonText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "600",
  },
  barcodeInput: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    paddingVertical: 8,
    paddingHorizontal: 12,
    fontSize: 16,
    textAlign: "right",
    outlineStyle: "none" as any,
  },
  unregisteredSection: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  unregisteredInputContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
    overflow: "hidden",
  },
  inlinePlusButton: {
    backgroundColor: "#FFFFFF",
    borderWidth: 2,
    borderColor: "#359D4D",
    borderRadius: 50,
    width: 32,
    height: 32,
    alignItems: "center",
    justifyContent: "center",
    marginHorizontal: 8,
  },
  plusButton: {
    backgroundColor: "#E0E0E0",
    borderRadius: 20,
    width: 36,
    height: 36,
    alignItems: "center",
    justifyContent: "center",
  },
  plusIcon: {
    width: 18,
    height: 18,
  },
  plusButtonText: {
    fontSize: 20,
    color: "#359D4D",
    fontWeight: "bold",
    lineHeight: 22,
  },
  unregisteredInput: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    paddingVertical: 8,
    paddingHorizontal: 12,
    fontSize: 14,
    textAlign: "right",
    outlineStyle: "none" as any,
  },
  productGridContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 4,
    elevation: 2,
    minHeight: 200,
  },
  productGridScroll: {
    maxHeight: 400,
  },
  emptyProductsContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    padding: 40,
  },
  emptyProductsText: {
    color: "#999",
    fontSize: 16,
    textAlign: "center",
  },
  scannerStatus: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    paddingHorizontal: 4,
  },
  scannerDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: "#4CAF50",
  },
  scannerStatusText: {
    fontSize: 14,
    color: "#666",
  },
  loadingContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 60,
  },
  productGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
    justifyContent: "flex-start",
  },
  productCard: {
    width: "calc(20% - 10px)" as any,
    minWidth: 140,
    maxWidth: 180,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    overflow: "hidden",
    borderWidth: 2,
    borderColor: "#E0F7E9",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 4,
    elevation: 2,
  },
  productImage: {
    width: "100%",
    height: 100,
  },
  productImagePlaceholder: {
    width: "100%",
    height: 100,
    backgroundColor: "#F0F0F0",
    alignItems: "center",
    justifyContent: "center",
  },
  noImageText: {
    color: "#999",
    fontSize: 14,
  },
  productName: {
    fontSize: 16,
    fontWeight: "500",
    color: "#333",
    paddingHorizontal: 8,
    paddingTop: 8,
    textAlign: "right",
  },
  productPriceRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    padding: 8,
  },
  productAddBtn: {
    backgroundColor: "#42B0DB",
    borderRadius: 4,
    paddingVertical: 4,
    paddingHorizontal: 12,
  },
  productAddBtnText: {
    color: "#FFFFFF",
    fontSize: 14,
  },
  productPrice: {
    fontSize: 14,
    color: "#666",
  },
});

export default WebSellScreen;
